import React, { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_CHECKLISTS = {
  Saved:       ['Research the company', 'Tailor resume to job description', 'Write cover letter', 'Check referral network'],
  Applied:     ['Research company culture', 'Tailor resume to job description', 'Write personalized cover letter', 'Connect with employee on LinkedIn'],
  Screen:      ['Review job description again', 'Prepare 2-min intro pitch', 'Research company news', 'Have questions ready'],
  Interviewing:['Prepare STAR method stories', 'Research your interviewer', 'Prepare 5 questions to ask', 'Do a mock interview', 'Plan logistics'],
  Offer:       ['Evaluate total comp package', 'Research market salary rates', 'Review benefits (health, 401k, PTO)', 'Negotiate if needed', 'Review offer letter carefully'],
  Accepted:    ['Sign and return offer letter', 'Set start date', 'Resign from current role', 'Complete background check / onboarding'],
  Rejected:    ['Send thank-you / feedback request email', 'Keep connection on LinkedIn', 'Note lessons learned'],
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function docIcon(mime) {
  if (!mime) return '📄';
  if (mime === 'application/pdf') return '📕';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime.includes('word')) return '📝';
  return '📄';
}

function Stars({ value, onChange }) {
  return (
    <div className="stars">
      {[1,2,3,4,5].map(n => (
        <span key={n} className={`star ${n <= value ? 'on' : ''}`} onClick={() => onChange(n === value ? 0 : n)}>
          {n <= value ? '★' : '☆'}
        </span>
      ))}
    </div>
  );
}

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fillTemplate(body, app) {
  return body
    .replace(/{{company}}/g, app.company || '')
    .replace(/{{role}}/g, app.role || '')
    .replace(/{{contact_name}}/g, 'Hiring Manager');
}

export default function ApplicationDetail({ app, contacts, allContacts = [], templates, API_URL, onClose, onUpdate, onEdit, onDelete, onContactsChange, expand = false }) {
  const [tab, setTab] = useState('overview');
  const [notes, setNotes] = useState(app.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [checklist, setChecklist] = useState(() => {
    const stored = Array.isArray(app.checklist) ? app.checklist : [];
    if (stored.length > 0) return stored;
    return (DEFAULT_CHECKLISTS[app.status] || []).map((text, i) => ({ id: String(i), text, completed: false }));
  });
  const [newItem, setNewItem] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [linkingContact, setLinkingContact] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const fileInputRef = useRef(null);
  const notesTimer = useRef(null);
  const secOverview = useRef(null);
  const secNotes = useRef(null);
  const secChecklist = useRef(null);
  const secSkills = useRef(null);
  const secTemplates = useRef(null);
  const secContacts = useRef(null);
  const expandBodyRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref.current && expandBodyRef.current) {
      const bodyTop = expandBodyRef.current.getBoundingClientRect().top;
      const secTop = ref.current.getBoundingClientRect().top;
      expandBodyRef.current.scrollBy({ top: secTop - bodyTop - 12, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (tab === 'documents') {
      fetch(`${API_URL}/api/documents/${app.id}`)
        .then(r => r.json())
        .then(d => setDocuments(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [tab, app.id, API_URL]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/documents/${app.id}`, { method: 'POST', body: fd });
        if (res.ok) {
          const doc = await res.json();
          setDocuments(prev => [doc, ...prev]);
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await fetch(`${API_URL}/api/documents/${docId}`, { method: 'DELETE' });
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const linkContact = async (contactId) => {
    if (!contactId) return;
    const contact = allContacts.find(c => c.id === parseInt(contactId));
    if (!contact) return;
    const res = await fetch(`${API_URL}/api/contacts/${contactId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...contact, application_id: app.id }),
    });
    if (res.ok) {
      const updated = await res.json();
      onContactsChange(prev => prev.map(c => c.id === updated.id ? { ...updated, app_company: app.company, app_role: app.role, app_id: app.id } : c));
    }
    setLinkingContact(false);
  };

  const unlinkContact = async (contactId) => {
    const contact = allContacts.find(c => c.id === contactId);
    if (!contact) return;
    const res = await fetch(`${API_URL}/api/contacts/${contactId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...contact, application_id: null }),
    });
    if (res.ok) {
      const updated = await res.json();
      onContactsChange(prev => prev.map(c => c.id === updated.id ? { ...updated, app_company: null, app_role: null } : c));
    }
  };

  const renameDocument = async (docId, currentName) => {
    const newName = window.prompt('Rename document:', currentName);
    if (!newName || newName.trim() === currentName) return;
    const res = await fetch(`${API_URL}/api/documents/${docId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ original_name: newName.trim() }),
    });
    if (res.ok) {
      const updated = await res.json();
      setDocuments(prev => prev.map(d => d.id === docId ? updated : d));
    }
  };

  useEffect(() => {
    setNotes(app.notes || '');
  }, [app.id]); // eslint-disable-line

  const autoSaveNotes = useCallback((value) => {
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        const res = await fetch(`${API_URL}/api/applications/${app.id}/notes`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: value }),
        });
        onUpdate(await res.json());
        setNotesSaved(true);
        setTimeout(() => setNotesSaved(false), 2000);
      } finally {
        setSavingNotes(false);
      }
    }, 1000);
  }, [app.id, API_URL, onUpdate]);

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    autoSaveNotes(e.target.value);
  };

  const saveChecklist = async (updated) => {
    setChecklist(updated);
    const res = await fetch(`${API_URL}/api/applications/${app.id}/checklist`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checklist: updated }),
    });
    onUpdate(await res.json());
  };

  const toggleItem = (id) => {
    saveChecklist(checklist.map(i => i.id === id ? { ...i, completed: !i.completed } : i));
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    const updated = [...checklist, { id: Date.now().toString(), text: newItem.trim(), completed: false }];
    saveChecklist(updated);
    setNewItem('');
  };

  const removeItem = (id) => saveChecklist(checklist.filter(i => i.id !== id));

  const extractKeywords = async () => {
    if (!app.job_description) {
      alert('No job description found. Add one in the Edit modal (Description tab) first.');
      return;
    }
    setExtracting(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/${app.id}/extract-keywords`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      onUpdate(await res.json());
    } catch { alert('Keyword extraction failed.'); }
    finally { setExtracting(false); }
  };

  const copyTemplate = async (tmpl) => {
    const body = fillTemplate(tmpl.body, app);
    const subject = fillTemplate(tmpl.subject, app);
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    setCopiedId(tmpl.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const done = checklist.filter(i => i.completed).length;
  const keywords = Array.isArray(app.keywords) ? app.keywords : [];

  // ── Expand mode: all sections in one scrollable page, no tabs ────────
  if (expand) {
    return (
      <>
        <div className="detail-overlay" onClick={onClose} />
        <div className="detail-panel expand-panel">
          <div className="detail-header">
            <div>
              <div className="detail-company">{app.company}</div>
              <div className="detail-role">{app.role}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={() => onEdit(app)}>✏️ Edit</button>
              <button className="detail-close" onClick={onClose}>✕</button>
            </div>
          </div>

          {/* Section nav */}
          <div className="expand-nav">
            <button className="expand-nav-btn" onClick={() => scrollToSection(secOverview)}>Overview</button>
            <button className="expand-nav-btn" onClick={() => scrollToSection(secNotes)}>Notes</button>
            <button className="expand-nav-btn" onClick={() => scrollToSection(secChecklist)}>
              Checklist <span className="expand-nav-badge">{done}/{checklist.length}</span>
            </button>
            <button className="expand-nav-btn" onClick={() => scrollToSection(secSkills)}>
              Skills {keywords.length > 0 && <span className="expand-nav-badge">{keywords.length}</span>}
            </button>
            {templates.length > 0 && (
              <button className="expand-nav-btn" onClick={() => scrollToSection(secTemplates)}>Email Templates</button>
            )}
            {contacts.length > 0 && (
              <button className="expand-nav-btn" onClick={() => scrollToSection(secContacts)}>
                Contacts <span className="expand-nav-badge">{contacts.length}</span>
              </button>
            )}
          </div>

          <div className="expand-body" ref={expandBodyRef}>
            {/* Overview */}
            <div className="expand-section" ref={secOverview}>
              <div className="expand-section-title">Overview</div>
              <div className="detail-grid">
                <div className="detail-field">
                  <div className="detail-field-label">Status</div>
                  <span className={`status-badge ${app.status.toLowerCase()}`}>{app.status}</span>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Location</div>
                  <div className="detail-field-value">{app.location || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Salary</div>
                  <div className="detail-field-value">{app.salary || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Job Type</div>
                  <div className="detail-field-value">{app.job_type || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Work Mode</div>
                  <div className="detail-field-value">{app.remote_type || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Applied</div>
                  <div className="detail-field-value">{fmt(app.applied_date)}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Deadline</div>
                  <div className="detail-field-value" style={app.deadline ? { color: '#f59e0b' } : {}}>{fmt(app.deadline)}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Degree</div>
                  <div className="detail-field-value">{app.required_degree || '—'}</div>
                </div>
              </div>
              {app.job_url && (
                <div className="detail-field" style={{ marginTop: '8px' }}>
                  <div className="detail-field-label">Job URL</div>
                  <a href={app.job_url} target="_blank" rel="noreferrer" className="detail-field-link">
                    {app.job_url.length > 70 ? app.job_url.slice(0, 70) + '…' : app.job_url}
                  </a>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="expand-section" ref={secNotes}>
              <div className="expand-section-title">Notes</div>
              <textarea
                className="notes-area"
                value={notes}
                onChange={handleNotesChange}
                placeholder="Add notes — recruiter contact, interview prep, referral details…"
              />
              <div className="save-indicator">
                {savingNotes ? '💾 Saving…' : notesSaved ? '✓ Saved' : ''}
              </div>
            </div>

            {/* Checklist */}
            <div className="expand-section" ref={secChecklist}>
              <div className="expand-section-title">Checklist <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.82rem' }}>{done}/{checklist.length} done</span></div>
              {checklist.map(item => (
                <div key={item.id} className="checklist-item">
                  <input type="checkbox" id={`exp-chk-${item.id}`} checked={item.completed} onChange={() => toggleItem(item.id)} />
                  <label htmlFor={`exp-chk-${item.id}`} className={item.completed ? 'done' : ''}>{item.text}</label>
                  <button className="remove-btn" onClick={() => removeItem(item.id)}>✕</button>
                </div>
              ))}
              <div className="add-checklist-row">
                <input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()} placeholder="Add a task…" />
                <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }} onClick={addItem}>Add</button>
              </div>
            </div>

            {/* Skills */}
            <div className="expand-section" ref={secSkills}>
              <div className="expand-section-title">Skills</div>
              {keywords.length > 0 ? (
                <div className="chips">
                  {keywords.map(k => (
                    <span key={k} className="kw-chip">
                      {k}
                      <button onClick={async () => {
                        const updated = keywords.filter(x => x !== k);
                        const res = await fetch(`${API_URL}/api/applications/${app.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...app, keywords: updated }),
                        });
                        onUpdate(await res.json());
                      }}>✕</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px' }}>No keywords yet.</p>
              )}
              <button className="extract-btn" onClick={extractKeywords} disabled={extracting}>
                {extracting ? '⏳ Extracting…' : '✨ Extract Keywords from JD'}
              </button>
            </div>

            {/* Contacts */}
            {contacts.length > 0 && (
              <div className="expand-section" ref={secContacts}>
                <div className="expand-section-title">Linked Contacts</div>
                {contacts.map(c => (
                  <div key={c.id} className="contact-mini">
                    <div className="contact-avatar">{(c.name || '?').charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="contact-mini-name">{c.name}</div>
                      <div className="contact-mini-title">{c.title}{c.company ? ` · ${c.company}` : ''}</div>
                      {c.email && <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '2px' }}>{c.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Templates */}
            {templates.length > 0 && (
              <div className="expand-section" ref={secTemplates}>
                <div className="expand-section-title">Email Templates</div>
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '10px' }}>Click to copy with this job's details filled in.</p>
                {templates.map(t => (
                  <div key={t.id} className="template-card" onClick={() => copyTemplate(t)}>
                    <div className="template-card-name">{t.name}</div>
                    <div className="template-card-stage">{t.stage}</div>
                    {copiedId === t.id && <div className="copied-msg">✓ Copied!</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="detail-footer">
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
              {app.job_url && (
                <a href={app.job_url} target="_blank" rel="noreferrer" className="btn-primary" style={{ textDecoration: 'none' }}>
                  View Job ↗
                </a>
              )}
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="detail-overlay" onClick={onClose} />
      <div className="detail-panel">
        {/* Header */}
        <div className="detail-header">
          <div>
            <div className="detail-company">{app.company}</div>
            <div className="detail-role">{app.role}</div>
          </div>
          <button className="detail-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'notes', label: 'Notes' },
            { id: 'checklist', label: `Checklist ${done}/${checklist.length}` },
            { id: 'keywords', label: `Skills ${keywords.length > 0 ? keywords.length : ''}` },
            { id: 'contacts', label: `Contacts ${contacts.length > 0 ? contacts.length : ''}` },
            { id: 'documents', label: `Docs ${documents.length > 0 ? documents.length : ''}` },
            { id: 'templates', label: 'Templates' },
          ].map(t => (
            <button key={t.id} className={`detail-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="detail-body">
          {/* ── Overview ── */}
          {tab === 'overview' && (
            <>
              <div className="detail-grid">
                <div className="detail-field">
                  <div className="detail-field-label">Status</div>
                  <span className={`status-badge ${app.status.toLowerCase()}`}>{app.status}</span>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Excitement</div>
                  <Stars value={app.excitement_rating || 0} onChange={async r => {
                    const res = await fetch(`${API_URL}/api/applications/${app.id}/rating`, {
                      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ rating: r }),
                    });
                    onUpdate(await res.json());
                  }} />
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Location</div>
                  <div className="detail-field-value">{app.location || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Salary</div>
                  <div className="detail-field-value">{app.salary || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Job Type</div>
                  <div className="detail-field-value">{app.job_type || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Work Mode</div>
                  <div className="detail-field-value">{app.remote_type || '—'}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Applied</div>
                  <div className="detail-field-value">{fmt(app.applied_date)}</div>
                </div>
                <div className="detail-field">
                  <div className="detail-field-label">Deadline</div>
                  <div className="detail-field-value" style={app.deadline ? { color: '#f59e0b' } : {}}>
                    {fmt(app.deadline)}
                  </div>
                </div>
              </div>
              {app.job_url && (
                <div className="detail-field" style={{ marginTop: '8px' }}>
                  <div className="detail-field-label">Job URL</div>
                  <a href={app.job_url} target="_blank" rel="noreferrer" className="detail-field-link">
                    {app.job_url.length > 55 ? app.job_url.slice(0, 55) + '...' : app.job_url}
                  </a>
                </div>
              )}
              {app.company_size && (
                <div className="detail-field" style={{ marginTop: '8px' }}>
                  <div className="detail-field-label">Company Size</div>
                  <div className="detail-field-value">{app.company_size} employees</div>
                </div>
              )}
            </>
          )}

          {/* ── Notes ── */}
          {tab === 'notes' && (
            <>
              <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '8px' }}>
                Auto-saves 1 second after you stop typing.
              </p>
              <textarea
                className="notes-area"
                value={notes}
                onChange={handleNotesChange}
                placeholder="Add notes — recruiter contact, interview prep, referral details, follow-up tasks..."
              />
              <div className="save-indicator">
                {savingNotes ? '💾 Saving...' : notesSaved ? '✓ Saved' : ''}
              </div>
            </>
          )}

          {/* ── Checklist ── */}
          {tab === 'checklist' && (
            <>
              <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '12px' }}>
                {done}/{checklist.length} tasks complete
              </p>
              {checklist.map(item => (
                <div key={item.id} className="checklist-item">
                  <input type="checkbox" id={`chk-${item.id}`} checked={item.completed} onChange={() => toggleItem(item.id)} />
                  <label htmlFor={`chk-${item.id}`} className={item.completed ? 'done' : ''}>{item.text}</label>
                  <button className="remove-btn" onClick={() => removeItem(item.id)}>✕</button>
                </div>
              ))}
              <div className="add-checklist-row">
                <input
                  value={newItem}
                  onChange={e => setNewItem(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addItem()}
                  placeholder="Add a task and press Enter..."
                />
                <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }} onClick={addItem}>Add</button>
              </div>
            </>
          )}

          {/* ── Keywords ── */}
          {tab === 'keywords' && (
            <div className="keywords-section">
              {keywords.length > 0 ? (
                <div className="chips">
                  {keywords.map(k => (
                    <span key={k} className="kw-chip">
                      {k}
                      <button onClick={async () => {
                        const updated = keywords.filter(x => x !== k);
                        const res = await fetch(`${API_URL}/api/applications/${app.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...app, keywords: updated }),
                        });
                        onUpdate(await res.json());
                      }}>✕</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px' }}>
                  No keywords yet. Add a job description and extract keywords.
                </p>
              )}
              <button className="extract-btn" onClick={extractKeywords} disabled={extracting}>
                {extracting ? '⏳ Extracting...' : '✨ Extract Keywords from JD'}
              </button>
              {!app.job_description && (
                <p style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '8px' }}>
                  ⚠ No job description saved. Edit this application → Description tab to add one.
                </p>
              )}
            </div>
          )}

          {/* ── Contacts ── */}
          {tab === 'contacts' && (
            <>
              {contacts.map(c => (
                <div key={c.id} className="contact-mini">
                  <div className="contact-avatar">{(c.name || '?').charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="contact-mini-name">{c.name}</div>
                    <div className="contact-mini-title">{c.title}{c.company ? ` · ${c.company}` : ''}</div>
                    {c.email && <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '2px' }}>{c.email}</div>}
                    {c.contact_status && c.contact_status !== 'Not Contacted' && (
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>{c.contact_status}</div>
                    )}
                  </div>
                  {onContactsChange && (
                    <button className="btn-icon" onClick={() => unlinkContact(c.id)} title="Unlink contact" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>✕</button>
                  )}
                </div>
              ))}

              {contacts.length === 0 && (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div className="empty-state-icon">👥</div>
                  <p style={{ fontSize: '0.85rem', color: '#64748b' }}>No contacts linked yet.</p>
                </div>
              )}

              {onContactsChange && (
                <div style={{ marginTop: '12px' }}>
                  {linkingContact ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        autoFocus
                        className="inline-select"
                        style={{ position: 'static', flex: 1, padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.84rem' }}
                        defaultValue=""
                        onChange={e => linkContact(e.target.value)}
                      >
                        <option value="" disabled>Select a contact to link…</option>
                        {allContacts
                          .filter(c => !contacts.find(lc => lc.id === c.id))
                          .map(c => (
                            <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}{c.type ? ` (${c.type})` : ''}</option>
                          ))
                        }
                      </select>
                      <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 10px' }} onClick={() => setLinkingContact(false)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn-secondary" style={{ fontSize: '0.82rem', width: '100%' }} onClick={() => setLinkingContact(true)}>
                      + Link Existing Contact
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Documents ── */}
          {tab === 'documents' && (
            <div className="documents-section">
              <input
                ref={fileInputRef}
                type="file"
                id="doc-upload"
                multiple
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.txt"
                style={{ display: 'none' }}
                onChange={handleUpload}
              />
              <label htmlFor="doc-upload" className="doc-upload-label">
                {uploading ? '⏳ Uploading...' : '📎 Click to upload documents'}
                <span>PDF, Word, Images, Text — up to 10MB each</span>
              </label>

              {documents.length === 0 && !uploading && (
                <p style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', marginTop: '16px' }}>
                  No documents uploaded yet.
                </p>
              )}

              {documents.map(doc => (
                <div key={doc.id} className="doc-item">
                  <span className="doc-icon">{docIcon(doc.mime_type)}</span>
                  <div className="doc-info">
                    <div className="doc-name">{doc.original_name}</div>
                    <div className="doc-meta">
                      {formatFileSize(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <div className="doc-actions">
                    <a
                      href={`${API_URL}/api/documents/file/${doc.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-icon"
                      title="View / Download"
                    >👁</a>
                    <button className="btn-icon" onClick={() => renameDocument(doc.id, doc.original_name)} title="Rename">✏️</button>
                    <button className="btn-icon" onClick={() => deleteDocument(doc.id)} title="Delete">🗑️</button>
                  </div>
                </div>
              ))}

              {viewingDoc && (
                <div className="doc-viewer-overlay" onClick={() => setViewingDoc(null)}>
                  <div className="doc-viewer" onClick={e => e.stopPropagation()}>
                    <div className="doc-viewer-header">
                      <span>{viewingDoc.original_name}</span>
                      <button onClick={() => setViewingDoc(null)}>✕</button>
                    </div>
                    {viewingDoc.mime_type?.startsWith('image/') ? (
                      <img src={`${API_URL}/api/documents/file/${viewingDoc.id}`} alt={viewingDoc.original_name} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                    ) : (
                      <iframe src={`${API_URL}/api/documents/file/${viewingDoc.id}`} title={viewingDoc.original_name} style={{ width: '100%', height: '70vh', border: 'none' }} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Templates ── */}
          {tab === 'templates' && (
            <>
              <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '12px' }}>
                Click a template to copy it to your clipboard with this job's details filled in.
              </p>
              {templates.map(t => (
                <div key={t.id} className="template-card" onClick={() => copyTemplate(t)}>
                  <div className="template-card-name">{t.name}</div>
                  <div className="template-card-stage">{t.stage}</div>
                  {copiedId === t.id && <div className="copied-msg">✓ Copied to clipboard!</div>}
                </div>
              ))}
              {templates.length === 0 && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>No templates yet. Add them on the Templates page.</p>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="detail-footer">
          <button className="btn-secondary" onClick={() => onEdit(app)}>✏️ Edit</button>
          <button className="btn-danger" onClick={() => onDelete(app.id)}>Delete</button>
          {app.job_url && (
            <a href={app.job_url} target="_blank" rel="noreferrer" className="btn-primary" style={{ textDecoration: 'none', marginLeft: 'auto' }}>
              View Job ↗
            </a>
          )}
        </div>
      </div>
    </>
  );
}
